import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { toast, ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import axios from "axios";
import formatDate from "../utils/formatDate";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import UserProfileForm from "./UserProfileForm"; // Import the new component

const UserProfile = () => {
  const userData = useSelector((state) => state?.user);

  const [deactivateStatus, setDeactivateStatus] = useState(true);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const { user, accessCodeList } = location.state || {};
  const [updatedUser, setUpdatedUser] = useState(user);
  const { id } = useParams();
  const businessIdentifier = userData.user?.businessIdentifier;
  const [updatedAccessCodeList, setUpdatedAccessCodeList] =
    useState(accessCodeList);
  const navigate = useNavigate();

  if (!user) {
    return <div>No user data available</div>;
  }

  const handleDeactivate = () => {
    setDeactivateStatus(false);
    setShowModal(true);
  };

  const handleActivate = async () => {
    setDeactivateStatus(true);
    setIsDeactivating(true);

    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/activateuser/${id}`,
        { id }
      );

      if (request.data.success) {
        toast.success(request.data.message);
        setTimeout(() => {
          navigate("/users");
        }, 3000);
      } else {
        toast.error("Could not activate user.");
        setIsDeactivating(false);
      }
    } catch (error) {
      toast.error(
        "Network connection lost. Please check your connection and try again."
      );
      setIsDeactivating(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirmDeactivate = async () => {
    setIsDeactivating(true);
    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/users/deactivateuser/${id}`,
        { id }
      );
      if (request.data.success) {
        toast.success(request.data.message);
        setTimeout(() => {
          navigate("/users");
        }, 3000);
      } else {
        toast.error("Could not deactivate user.");
        setIsDeactivating(false);
      }
    } catch (error) {
      toast.error(error.response.data.message);
      setIsDeactivating(false);
    }
    setShowModal(false);
  };

  const handleEditToggle = () => {
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({ ...updatedUser, [name]: value });
  };

  return (
    <>
      {!isEditing ? (
        <div style={{ display: "flex", gap: "20px" }}>
          <div style={styles.container}>
            <div style={styles.header}>
              <h2 style={styles.title}>User Profile</h2>
              <div style={styles.buttonContainer}>
              <>
                <button
                  disabled={isDeactivating}
                  onClick={handleDeactivate}
                  style={styles.deactivateButton}
                >
                  {isDeactivating ? "Deactivate" : "Deactivate"}
                </button>
                <button
                  onClick={handleEditToggle}
                  style={styles.editButton}
                >
                  Edit
                </button>
              </>
              </div>
            </div>
            <div style={styles.infoContainer}>
              <div style={styles.infoRow}>
                <span style={styles.span}>ID</span>
                <span style={styles.span}>
                  {user.userAutoGenId != null && user.userAutoGenId !== ""
                    ? String(user.userAutoGenId).padStart(4, "0")
                    : user.cocmMeta?.patientId || "—"}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.span}>Status</span>
                <span style={styles.span}>
                  {user.isactive ? "Active" : "Inactive"}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.span}>First Name</span>
                <span style={styles.span}>{user.firstname}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.span}>Last Name</span>
                <span style={styles.span}>{user.lastname}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.span}>Phone Number</span>
                <span style={styles.span}>
                  {formatPhoneNumber(user.phoneno)}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.span}>Email</span>
                <span style={styles.span}>{user.email}</span>
              </div>
              {userData?.user?.role !== "business" && (
                <div style={styles.infoRow}>
                  <span style={styles.span}>Birth Date</span>
                  <span style={styles.span}>{formatDate(user.dob)}</span>
                </div>
              )}
              <div style={styles.infoRow}>
                <span style={styles.span}>Zip Code</span>
                <span style={styles.span}>{user.zipcode}</span>
              </div>
              {businessIdentifier ? (
                <>
                  <div style={styles.infoRow}>
                    <span style={styles.span}>Access Code</span>
                    <span style={styles.span}>{user.access_codes[0]}</span>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>
            {/* Modal component */}
            {showModal && (
              <div style={styles.modalOverlay}>
                <div style={styles.modalContainer}>
                  <p style={styles.modalText}>
                    Are you sure you want to <br /> deactivate this user?
                  </p>
                  <button
                    disabled={isDeactivating}
                    onClick={handleConfirmDeactivate}
                    style={styles.confirmButton}
                  >
                    {isDeactivating ? "Deactivate" : "Yes, deactivate"}
                  </button>
                  <button
                    disabled={isDeactivating}
                    onClick={handleCloseModal}
                    style={styles.cancelButton}
                  >
                    No, keep active
                  </button>
                </div>
              </div>
            )}
            <ToastContainer />
          </div>
          {!businessIdentifier && (
            <div style={styles.container}>
              <div style={styles.header}>
                <h2 style={styles.title}>Linked Access Codes</h2>
              </div>
              <div style={styles.infoContainer}>
                {updatedAccessCodeList && updatedAccessCodeList.length > 0 ? (
                  <table
                    className="LinkedAccessCode"
                    style={{ borderCollapse: "collapse", width: "100%" }}
                  >
                    <tbody>
                      {updatedAccessCodeList.map((accessCodeItem, index) => (
                        <tr key={index} style={styles.tr}>
                          <td style={styles.td} className="tdFont ps-35">
                            {"Company " +
                              accessCodeItem.business?.businessIdentifier ||
                              "N/A"}
                          </td>
                          <td style={styles.td} className="tdFont">
                            {accessCodeItem.accesscode}
                          </td>
                          <td
                            className="text-center pe-35"
                            style={{
                              ...styles.td,
                              width: "100px",
                            }}
                          >
                            {user.isactive ? (
                              <p
                                className={`LinkedAccessCode StatusBox ${
                                  isDeactivating ||
                                  accessCodeItem.status !== "Active"
                                    ? "inActive"
                                    : ""
                                }`}
                              >
                                {isDeactivating
                                  ? "Inactive"
                                  : accessCodeItem.status}
                              </p>
                            ) : (
                              <p
                                className={`LinkedAccessCode StatusBox ${
                                  !isDeactivating ||
                                  accessCodeItem.status !== "Active"
                                    ? "inActive"
                                    : ""
                                }`}
                              >
                                {!isDeactivating
                                  ? "Inactive"
                                  : accessCodeItem.status}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={styles.noData}>No access codes available</div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <UserProfileForm
          user={updatedUser}
          handleInputChange={handleInputChange}
        />
      )}
    </>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    border: "0px solid #ccc",
    borderRadius: "8px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    fontFamily: "Poppins",
    overflow: "hidden",
    width: "600px",
  },
  header: {
    alignItems: "center",
    backgroundColor: "#064260",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    padding: "20px 35px",
  },
  title: {
    fontSize: "21px",
    fontWeight: 600,
    margin: 0,
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
  },
  deactivateButton: {
    backgroundColor: "#ED3A3A",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
  activateButton: {
    backgroundColor: "#27B99A",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
  editButton: {
    backgroundColor: "#27B99A",
    border: "none",
    borderRadius: "35px",
    color: "#fff",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: 500,
    padding: "8px 10px",
    width: "80px",
  },
  infoContainer: {
    border: "0",
    padding: "0",
  },
  infoRow: {
    borderBottom: "1px solid #eee",
    display: "flex",
    fontSize: "1em",
    justifyContent: "space-between",
    padding: "20px 35px",
  },
  span: {
    fontSize: "17px",
    fontWeight: 600,
    margin: 0,
  },
  modalOverlay: {
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "fixed",
    right: 0,
    top: 0,
  },
  modalContainer: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "396px",
    padding: "35px 46px 55px 46px",
    textAlign: "center",
  },
  modalText: {
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: "1.5",
    wordWrap: "break-word",
  },
  confirmButton: {
    border: "none",
    color: "#064260",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    padding: "10px 15px",
  },
  cancelButton: {
    border: "none",
    color: "#064260",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    padding: "10px 15px",
  },
  td: {
    borderBottom: "1px solid #ddd",
    padding: "19px 10px",
    textAlign: "left",
  },
};

export default UserProfile;
